import React, { useContext, useEffect, useState } from 'react';
import { useSnackbar } from 'notistack';
import { Alert, AlertTitle, Box, Dialog, DialogTitle, DialogContent, DialogActions, Button, TextField, Typography, Slide } from '@mui/material';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';

import ProjectContext from '../../../contexts/ProjectContext';

import ExpandedDropDown from '../../dropdown/ExpandedDropDown';
import FileDropBox from '../../filedropbox/FileDropBox';
import UploadMetadataListDatagrid from './UploadMetadataListDatagrid';
import {useFetch} from '../../../components/auth/useFetch';


function UploadMetadataDialog(props) {
    const { getLinkSummary, open, setOpen, sources, transferdate, updateParentList } = props
    const { enqueueSnackbar } = useSnackbar();
    const projectContext = useContext(ProjectContext);
    const [fileList, setFileList] = useState([])
    const [source, setSource] = useState({})
    const [isUploading, setIsUploading] = useState(false)
    const fetchWithAuth = useFetch()


    useEffect(() => {
        setSource(sources[0])
    }, [sources])


    const fileDrop = (files) => {
        const droppedFiles = files
            .filter(file => file.name.toLowerCase().endsWith(source.extension.toLowerCase()))
            .map((file) => ({'name': file.name, 'source': source.name, 'file': file}))
        setFileList((prev) => {
            const filteredFiles = droppedFiles.filter((file) => {
                return !prev.some((prevFile) =>
                    prevFile.name === file.name && prevFile.source === file.source
                );
            });
            return [...prev, ...filteredFiles];
        })
    }

    const uploadFile = async (file) => {
        const formData = new FormData();
        formData.append("file_upload", file.file);
        formData.append("last_mod_date", file.file.lastModifiedDate)
        formData.append("source", file.source);
        formData.append("transfer_date", transferdate);
        var requestOptions = {
            method: 'POST',
            body: formData,
            headers: {
                'X-Project-ID': projectContext?.project_db
            },
        }
        const url = `${process.env.REACT_APP_BACKEND_URL}/konsol/uploadfile/`
        try
        {
            const response = await fetchWithAuth(url, requestOptions)
            const result = await response.json()
            if(!response.ok){
                throw new Error(result?.message ?? '');
            }
                if(result['result'] == 'Safe') {
                    enqueueSnackbar(`File ${file.name} uploaded successfully: ${result.message}`, { variant: 'success' });
                } else if (result['result'] == 'Virus') {
                    enqueueSnackbar(`File ${file.name} uploaded successfully: ${result.message}`, { variant: 'warning' });
                }
                return true;
        }
        catch(e){
            console.error(e);
            const errorMessage = e?.message ? `: ${e?.message}` : '';
            enqueueSnackbar(`File ${file.name} failed to upload ${errorMessage}`, { variant: 'error' });
            return false;
        }
    }


   
    const uploadFiles = async () => {
        setIsUploading(true)

        // If more than 7 files, upload sequentially to avoid overwhelming the server
        //   Important for eg Framedata on ECS instances where there is limited memory and CPU
        let results = []
        if (fileList.length > 7){ 
            for (const item of fileList) {  
                const result = await uploadFile(item)
                results.push(result)
            }  
        }else{
            results = await Promise.all(fileList.map(uploadFile))
        }
 
        setIsUploading(false)
        const uploadedFileList = [...fileList.filter((_, index) => results[index])] 
        const failedFileList = [...fileList.filter((_, index) => !results[index])]
        getLinkSummary(transferdate)
        updateParentList((prev) => {
            return [...prev, ...uploadedFileList.map((file, index) => {
                const { name, source, ...fileProp } = file
                return {filename: name, source: source, id: prev.length + index, upload_datetime: '-'}
            })]
        })
        setFileList((prev) => {
            return [...failedFileList]
        })
    }

    const clearFile = (index) => {
        setFileList((prev) => {
            prev.splice(index, 1)
            return [...prev]
        })
    }

    const clearAllFiles = () => {
        setFileList([])
    }

    const handleSourceChange = (event) => {
        const selectedSource = event.target.value
        setSource(sources.find(src => src.name === selectedSource) ?? sources[0])
    }

    const handleClose = () => {
        setOpen(false);
    }


    return (
        <React.Fragment>
            <Dialog 
                open={open} 
                onClose={handleClose}
                maxWidth="lg"
                PaperProps={{
                    sx: {
                        width: 'auto',
                        height: 'auto',
                        backgroundColor: '#282828'
                    }
                }} >
                <DialogTitle>Upload Metadata Files</DialogTitle>
                <DialogContent>
                    <ExpandedDropDown
                        label='Source'
                        options={Object.values(sources) ?? []}
                        value={source?.name ?? ''}
                        onChange={handleSourceChange}
                        fullWidth
                    />
                    <FileDropBox
                        titleText={'Drop Metadata file here'}
                        UploadIcon={<CloudUploadIcon sx={{ fontSize: 48, opacity: 0.4 }} />}
                        setFile={fileDrop}
                        multiple
                    />
                    <UploadMetadataListDatagrid
                        fileList={fileList}
                        sources={sources}
                        onDeleteFile={(fileToDelete) => {
                            setFileList((prev) => prev.filter((file) => file !== fileToDelete));
                        }}
                        isUploading={isUploading}
                    />
                </DialogContent>
                <DialogActions sx={{ display: 'flex', justifyContent: 'space-between'}}>
                    <Button 
                        onClick={clearAllFiles} 
                        sx={{ color: 'white' }}>
                        Clear All
                    </Button>
                    <Box>
                        <Button 
                            onClick={handleClose} 
                            sx={{ color: 'white' }}>
                            Close
                        </Button>
                        <Button 
                            onClick={uploadFiles} 
                            sx={{ color: 'white' }}>
                            Submit
                        </Button>
                    </Box>
                </DialogActions>
            </Dialog>
        </React.Fragment>
    );
}

export default UploadMetadataDialog;